import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ColDef, GridOptions, CellClassParams } from 'ag-grid-community';

import { agGridLocaleText } from '../../assets/vrm-language-scripts/ag-grid-locale-text';
import { CommonService } from './common.service';

interface SpecificColWidthSetting {
  columnId: string;
  width?: number;
  flex?: number;
}

interface AgGridConfigInternal {
  common: GridOptions;
  statusPanels: Array<any>;
  defaultColumnConfig: any;
  specificColWidth: Array<SpecificColWidthSetting>;
}

enum VrmColumnTypes {
  text = 'text',
  number = 'number',
  percentage = 'percentage',
  date = 'date',
  email = 'email',
}

export enum ReportNameEnum {
  profileInsights = 'Profile Insights',
}

enum ColIdEnum {
  id = 'id',
  title = 'title',
  asset = 'asset',
  category = 'category',
  company = 'company',
  lang = 'lang',
  extension = 'extension',
  createdAt = 'created_at',

  status = 'status',
  lastUpdated = 'lastUpdated',
  interventionType = 'interventionType',
  intervention = 'intervention',
  createdDate = 'createdDate',
  createdBy = 'createdBy',
  action = 'action',
  popout = 'popout',
  coachingMonth = 'coachingMonth',
  coachingReason = 'coachingReason',

  answer = 'answer',
}

export enum CasualDialogTypesEnum {
  guideViewer = 'guide-viewer',
}

@Injectable({ providedIn: 'root' })
export class CasualReportService {
  selectedRows: Array<any> = [];

  agGridConfig: AgGridConfigInternal = {
    common: {
      rowDragManaged: true,
      groupIncludeFooter: true,
      animateRows: true,
      multiSortKey: 'ctrl',
      headerHeight: 35, // <-- default
      rowHeight: 33,
      enableRangeSelection: true,
      localeText: null,
      autoSizePadding: 30, // <-- allows space for the sort icon
      enableRtl: false, // <-- This is set in the constructor when rtl is in use
    },
    statusPanels: [
      {
        statusPanel: 'agTotalRowCountComponent',
        align: 'left',
      },
      { statusPanel: 'agFilteredRowCountComponent' },
      { statusPanel: 'agSelectedRowCountComponent' },
    ],

    defaultColumnConfig: {
      sortable: true,
      resizable: true,
      enableRowGroup: true,
    },

    specificColWidth: [
      { columnId: ColIdEnum.extension, width: 100 },
      { columnId: ColIdEnum.intervention, width: 250 },
      { columnId: ColIdEnum.interventionType, width: 250 },
    ],
  };

  gridState = {
    sortModel: [],
  };

  constructor(private cService: CommonService, private translation: TranslateService) {
    this.initialize();
  }

  initialize() {
    // AgGrid Local translations
    this.translation.get('AGGRIDLOCALE').subscribe({
      next: (translate) => {
        const localeTextKeys = Object.keys(agGridLocaleText);

        localeTextKeys.forEach((localeKey) => {
          if (translate[localeKey]) {
            agGridLocaleText[localeKey] = translate[localeKey];
          }
        });

        this.agGridConfig.common.localeText = agGridLocaleText;
        this.agGridConfig.common.enableRtl =
          this.cService.selectedLanguage && this.cService.selectedLanguage.isRtl
            ? this.cService.selectedLanguage.isRtl
            : false;
      },
    });
  }

  convertToAgColumnDefinition(structure: any, reportName?: string): Array<ColDef> {
    const columnNames = Object.keys(structure);
    const agGridColumnDef: Array<ColDef> = [];

    const reportDataMapping = {
      Complete: this.cService.readyTranslations.reportData.complete,
      complete: this.cService.readyTranslations.reportData.complete,
      incomplete: this.cService.readyTranslations.reportData.incomplete,
      Incomplete: this.cService.readyTranslations.reportData.incomplete,
      'Not Started': this.cService.readyTranslations.reportData.notStarted,
      'Not started': this.cService.readyTranslations.reportData.notStarted,
      'not started': this.cService.readyTranslations.reportData.notStarted,
      Required: this.cService.readyTranslations.reportData.required,
      required: this.cService.readyTranslations.reportData.required,
      Optional: this.cService.readyTranslations.reportData.optional,
      optional: this.cService.readyTranslations.reportData.optional,
      'In Progress': this.cService.readyTranslations.reportData.inProgress,
      'In progress': this.cService.readyTranslations.reportData.inProgress,
      'in progress': this.cService.readyTranslations.reportData.inProgress,
      Closed: this.cService.readyTranslations.reportData.closed,
      closed: this.cService.readyTranslations.reportData.closed,
      NA: this.cService.readyTranslations.reportData.na,
      Na: this.cService.readyTranslations.reportData.na,

      January: this.cService.readyTranslations.reportData.january,
      February: this.cService.readyTranslations.reportData.february,
      March: this.cService.readyTranslations.reportData.march,
      April: this.cService.readyTranslations.reportData.april,
      May: this.cService.readyTranslations.reportData.may,
      June: this.cService.readyTranslations.reportData.june,
      July: this.cService.readyTranslations.reportData.july,
      August: this.cService.readyTranslations.reportData.august,
      September: this.cService.readyTranslations.reportData.september,
      October: this.cService.readyTranslations.reportData.october,
      November: this.cService.readyTranslations.reportData.november,
      December: this.cService.readyTranslations.reportData.december,

      'Coach Auto Assign': this.cService.readyTranslations.reportData.coachAutoAssign,
      'Coaching API Service': this.cService.readyTranslations.reportData.coachingApiService,

      'Not Available': this.cService.readyTranslations.reportData.notAvailable,
      weeklyFicoGreaterThan500: 'Weekly FICO Safe Driving Score <500',
      'Weekly FICO Safe Driving Score <559': 'Weekly FICO Safe Driving Score <559',
      ficoSafeScoreBetween560And709:
        'Any Driver who had a FICO® Safe Driving Score between 560-709 for each of the last two weeks',
      driverIndexCoachCoachingReason: 'Low FICO® Safe Driving Score',
      pccCoachingReason: 'Collision',
      driverIndexProCoachingReason: 'Low FICO® Safe Driving Score and had a Collision or Licence Event',
    };

    this.translation.get('REPORTCOLUMNS').subscribe({
      next: (translate) => {
        columnNames.forEach((col: string) => {
          const colHeaderName = structure[col].name;
          const colHeaderTooltipTag = structure[col].headerTooltip ? structure[col].headerTooltip : `${col}Tooltip`;

          const translatedColName: string = translate[colHeaderName] ? translate[colHeaderName] : colHeaderName;
          let translatedColHeaderTooltip: string =
            colHeaderTooltipTag && translate[colHeaderTooltipTag] ? translate[colHeaderTooltipTag] : translatedColName;

          // TODO: Done in a rush for Ecolab, may need translating
          // Also, it'll be ideal if the structure was returning a name for this report,
          // so that the following if-statement is tied to the report only
          if (col.toLowerCase() === ColIdEnum.status) {
            const requiredStatus = 'Required:\nA required coaching session that has not been completed \n\n';
            const inProgressStatus =
              'In Progress:\nA coaching session that has started but has not been completed \n\n';
            const completeStatus = 'Complete:\nA coaching session that has been completed \n\n';
            const closedStatus =
              'Closed:\nA required coaching session that was not completed during the coaching month';

            const statusToolTips = `${requiredStatus + inProgressStatus + completeStatus + closedStatus}`;

            translatedColHeaderTooltip = statusToolTips;
          }

          let synthBackendSortIndicator = '';
          let synthSortApplicableColId = '';

          // Show sort icon against back-end's default sort
          if (structure[col].defaultSort) {
            const sortDirectionIconClass: string =
              structure[col].defaultSort.direction == 'desc' ? 'ag-desc-sort-icon' : 'ag-asc-sort-icon';

            synthBackendSortIndicator = `<i id="dialogDummySortIndicator" class="${sortDirectionIconClass}"></i>`;
            synthSortApplicableColId = col;
          }

          const column: ColDef = {
            headerName: translatedColName,
            field: col,
            colId: col,
            filter: true,
            filterParams: { clearButton: true },
            headerComponentParams: {
              template: `
                <div class="ag-cell-label-container ag-tooltip-trigger" role="presentation" title="${translatedColHeaderTooltip}">
                  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>

                  <div ref="eLabel" class="ag-header-cell-label" role="presentation">

                    <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>

                    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>
                    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>
                    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>
                    ${col === synthSortApplicableColId ? synthBackendSortIndicator : ''}
                    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>

                    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                  </div>
                </div>
                `,
            },
          };

          // Set master column defaults
          if (structure[col].master) {
            column.pinned = 'left';
          }
          if (structure[col].pinned) {
            column.pinned = 'left';
          }
          if (column.pinned) {
            column.lockPinned = true;
            column.cellClass = 'lock-pinned';
          }

          if (col === ColIdEnum.title || col === ColIdEnum.asset) {
            column.pinned = 'left';
            // Render Title as Link-button
            column.cellRenderer = 'titleLinkButtonCasualReportComponent';
          }
          if (col === ColIdEnum.extension) {
            column.pinned = 'right';
            column.cellRenderer = 'fileExtensionAsIconComponent';
          }

          // Hide / persist hidden columns
          const columnHidden = col === ColIdEnum.id || col === ColIdEnum.company ? true : false;
          if (columnHidden) {
            column.hide = true;
          }

          // Apply case-insensitive comparator for sorting.
          if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.text) {
            column.comparator = this.caseInsensitiveSort;
          }

          // Apply percentage transformations
          if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.percentage) {
            column.cellRenderer = 'numberToPercentageComponent';

            // NOTE: Tagging this as numericColumn helps agGrid to align it right.
            // column.type = 'numericColumn';

            // Be graceful to reports with width set independently
            if (!structure[col].width) {
              column.width = 140;
            }
          }

          // Apply number type transformations
          if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.number) {
            column.cellRenderer = 'numberToDecimalPlaceComponent';
            column.aggFunc = 'vrmSum';
            column.enableValue = true;
            column.sortingOrder = ['desc', 'asc', null];

            // NOTE: Tagging this as numericColumn helps agGrid to align it right.
            // column.type = 'numericColumn';
            column.filter = 'agNumberColumnFilter';

            // Be graceful to reports with width set independently
            if (!structure[col].width) {
              column.width = 140;
            }
          }
          // Process specific width recommendations
          // NOTE: This must come after setting generic width for all numeric / percentage columns
          this.agGridConfig.specificColWidth.forEach((columnSetting: SpecificColWidthSetting) => {
            if (col === columnSetting.columnId) {
              column.width = columnSetting.width;
            }
          });

          // Apply date type transformations
          if (structure[col].type && structure[col].type.toLowerCase() === VrmColumnTypes.date) {
            column.filter = 'agDateColumnFilter';
            column.filterParams = {
              comparator: (filterLocalDateAtMidnight: number | any, cellValue: any) => {
                let finalDate: number = -1;
                const dateAsString = cellValue;
                if (dateAsString == null) {
                  finalDate = -1;
                }

                if (finalDate !== -1) {
                  const dateParts = dateAsString.split('/');
                  const cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
                  if (filterLocalDateAtMidnight.getTime() == cellDate.getTime()) {
                    finalDate = 0;
                  }
                  if (cellDate < filterLocalDateAtMidnight) {
                    finalDate = -1;
                  }
                  if (cellDate > filterLocalDateAtMidnight) {
                    finalDate = 1;
                  }
                }

                return finalDate;
              },

              browserDatePicker: true,
              clearButton: true,
            };
          }

          // Coaching Toolkit Grid
          if (col.toLowerCase() === ColIdEnum.status.toLowerCase()) {
            column.cellClass = (params: CellClassParams) => {
              let agGridText: string = '';

              if (typeof params.value === 'string') {
                if (params.value.toLowerCase().includes('new') || params.value.toLowerCase().includes('required')) {
                  agGridText = 'agGridRedTexts';
                } else if (params.value.toLowerCase().includes('progress')) {
                  agGridText = 'agGridAmberTexts';
                } else if (params.value.toLowerCase().includes('complete')) {
                  agGridText = 'agGridGreenTexts';
                } else if (params.value.toLowerCase().includes('optional')) {
                  agGridText = 'agGridGreenTexts';
                } else if (params.value.toLowerCase().includes('closed')) {
                  agGridText = 'agGridGreyTexts';
                }
              }

              return agGridText;
            };
          }

          if (
            reportName &&
            reportName.toLowerCase() === ReportNameEnum.profileInsights.toLowerCase() &&
            col === ColIdEnum.answer
          ) {
            column.cellClass = (params: CellClassParams) => {
              return typeof params.value == 'string' ? 'agGridRedTexts' : '';
            };
          }

          if (
            col.toLowerCase() === ColIdEnum.coachingMonth.toLowerCase() ||
            col.toLowerCase() === ColIdEnum.status.toLowerCase()
          ) {
            column.refData = reportDataMapping;
          }

          if (col.toLowerCase() === ColIdEnum.coachingReason.toLowerCase()) {
            column.valueGetter = (params) => {
              let coachingReason = '';

              if (params && params.data) {
                coachingReason = this.cService.getSingleTranslation(
                  'REPORTDATA.' + params.data.coachingReason,
                  params.data.coachingReason
                ) as string;
              }

              return coachingReason;
            };
          }
          const translateReportDataForColumns = [ColIdEnum.createdBy];

          translateReportDataForColumns.forEach((columnName) => {
            if (col.toLowerCase() === columnName.toLowerCase()) {
              column.valueGetter = function (params) {
                let translatedValue = null;

                if (params && params.data) {
                  if (reportDataMapping.hasOwnProperty(params.data[columnName])) {
                    translatedValue = reportDataMapping[params.data[columnName]];
                  } else {
                    translatedValue = params.data[columnName];
                  }
                }

                return translatedValue;
              };
            }
          });

          column.cellClassRules = {
            agGridAttentionRequired: (params: CellClassParams) => {
              return params.data && typeof params.data.status === 'string'
                ? params.data.status.toLowerCase().includes('new')
                : false;
            },
          };

          if (col.toLowerCase() === ColIdEnum.action) {
            if (reportName && reportName.toLowerCase() === 'profile insights') {
              column.cellRenderer = 'sendFileActionButtonComponent';
            } else {
              column.cellRenderer = 'actionOptionButtonCasualReportComponent';
            }
          }

          if (col.toLowerCase() === ColIdEnum.popout) {
            column.cellRenderer = 'openResourceInNewTabOrWindowComponent';
          }

          agGridColumnDef.push(column);
        });
      },
    });

    console.log('Transformed column structure', { agGridColumnDef, structure });
    return agGridColumnDef;
  }

  caseInsensitiveSort(valueA: any, valueB: any): number {
    if (!valueA) {
      valueA = '';
    }
    if (!valueB) {
      valueB = '';
    }

    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
  }
}
